import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb, escurecer, clarear,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

// ============================================================
// Relatório de Comissões — Marcki v1 (PAISAGEM)
// KPIs + comissão por produto + ranking por vendedor +
// resumo geral por vendedor + detalhamento por vendedor.
// ============================================================

const generatePdfRelatorioComissao = async (data, filtro) => {
  if (!data) return;

  const pdfDoc   = await PDFDocument.create();
  const fontReg  = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // PAISAGEM
  const W = 841.89; const H = 595.28;
  const ML = 40; const MR = 40;
  const CW = W - ML - MR;

  const C = {
    cinzaChumbo: rgbHex(coresHex.chumbo),
    cinzaMedio:  rgbHex(coresHex.cinzaMedio),
    cinzaClaro:  rgbHex(coresHex.cinzaClaro),
    laranja:     rgbHex(coresHex.laranja),
    laranjaClaro:rgbHex(coresHex.laranjaClaro),
    laranjaEsc:  rgbHex(coresHex.laranjaEscuro),
    branco:      rgb(1, 1, 1),
    preto:       rgb(0.133, 0.133, 0.133),
    borda:       rgbHex(coresHex.borda),
  };

  const brl = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const drawRect  = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width: w, height: h, color });
  const drawBRect = (pg, x, y, w, h, fill, border) => pg.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: border || C.borda, borderWidth: 0.5 });
  const drawText  = (pg, t, x, y, s, f, color) => pg.drawText(String(t ?? ''), { x, y, size: s, font: f, color });

  const tH = 15;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y - tH + 2, c.w, tH, c.fill, c.bc || C.borda);
      if (c.text != null && c.text !== '') {
        const sz = c.sz || 8; const fn = c.fn || fontReg;
        let tx = c.x + 4;
        if (c.al === 'r') tx = c.x + c.w - fn.widthOfTextAtSize(String(c.text), sz) - 4;
        if (c.al === 'c') tx = c.x + (c.w - fn.widthOfTextAtSize(String(c.text), sz)) / 2;
        drawText(pg, c.text, tx, y - tH + 5, sz, fn, c.cl || C.preto);
      }
    });
    return y - tH;
  };

  // ── Logo ────────────────────────────────────────────────────────────────
  let logoImage = null;
  let empresa = null;
  try {
    empresa = await getEmpresaAtual();
    if (empresa?.logoBranca) {
      const base64 = empresa.logoBranca.split(',')[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      logoImage = await pdfDoc.embedPng(bytes);
    }
  } catch (e) { /* sem logo */ }

  const drawHeader = (pg) => {
    drawRect(pg, 0, H - 44, W, 44, C.cinzaChumbo);
    drawRect(pg, 0, H - 44, 6, 44, C.laranja);
    if (logoImage) {
      const lDims = logoImage.scaleToFit(70, 28);
      pg.drawImage(logoImage, { x: ML, y: H - 38, width: lDims.width, height: lDims.height });
    } else {
      drawText(pg, empresa?.nome || 'Marcki Engenharia', ML, H - 26, 11, fontBold, C.branco);
    }
    const titulo = 'Relatório de Comissões';
    drawText(pg, titulo, W - MR - fontBold.widthOfTextAtSize(titulo, 9), H - 24, 9, fontBold, C.branco);
    const periodo = [
      filtro?.datainicial ? new Date(filtro.datainicial).toLocaleDateString('pt-BR') : '',
      filtro?.datafinal   ? new Date(filtro.datafinal).toLocaleDateString('pt-BR')   : '',
    ].filter(Boolean).join(' — ');
    if (periodo) drawText(pg, periodo, W - MR - fontReg.widthOfTextAtSize(periodo, 8), H - 36, 8, fontReg, rgb(0.8, 0.8, 0.8));
    return H - 58;
  };

  const drawFooter = (pg) => {
    drawRect(pg, 0, 0, W, 26, C.cinzaChumbo);
    drawRect(pg, 0, 0, 6, 26, C.laranja);
    drawText(pg, `${empresa?.nome || 'Marcki Engenharia'} — ${empresa?.enderecoRodape || 'Araguari/MG'}`, ML + 8, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
    drawText(pg, `Gerado em ${new Date().toLocaleDateString('pt-BR')}`, W - MR - 70, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
  };

  let page = pdfDoc.addPage([W, H]);
  let y = drawHeader(page);
  const novaPagina = (alturaMin = 60) => {
    if (y < alturaMin) {
      drawFooter(page);
      page = pdfDoc.addPage([W, H]);
      y = drawHeader(page);
      return true;
    }
    return false;
  };
  const secTitulo = (txt) => {
    drawRect(page, ML, y + 2, CW, 16, C.cinzaChumbo);
    drawText(page, txt, ML + 6, y + 7, 8, fontBold, C.branco);
    y -= 16;
  };

  const kpis        = data.kpis || { total: 0, qtdVendedores: 0, qtdVendas: 0, ticketMedio: 0 };
  const porProduto  = data.porProduto || [];
  const tabelaGeral = data.tabelaGeral || [];
  const detalhado   = data.detalhado || [];

  // ── KPIs ──────────────────────────────────────────────────────────────────
  y -= 8;
  const kpiGap = 6;
  const kpiW = (CW - kpiGap * 3) / 4;
  const kpiH = 38;
  const kpiArr = [
    { label: 'Total de Comissões', valor: brl(kpis.total) },
    { label: 'Vendedores',         valor: String(kpis.qtdVendedores) },
    { label: 'Vendas',             valor: String(kpis.qtdVendas) },
    { label: 'Ticket Médio',       valor: brl(kpis.ticketMedio) },
  ];
  kpiArr.forEach((kpi, i) => {
    const x = ML + i * (kpiW + kpiGap);
    drawBRect(page, x, y - kpiH, kpiW, kpiH, C.laranjaClaro, C.laranja);
    drawRect(page, x, y - kpiH, 3, kpiH, C.laranja);
    drawText(page, kpi.label, x + 6, y - 14, 7.5, fontReg, C.cinzaMedio);
    drawText(page, kpi.valor, x + 6, y - 30, kpi.valor.length > 14 ? 9 : 11, fontBold, C.cinzaChumbo);
  });
  y -= kpiH + 16;

  // ── Comissão por Produto ──────────────────────────────────────────────────
  if (porProduto.length > 0) {
    secTitulo('COMISSÃO POR PRODUTO');
    const totalProd = porProduto.reduce((s, p) => s + (p.value || 0), 0) || 1;
    const p1 = CW * 0.50; const p2 = CW * 0.25; const p3 = CW - p1 - p2;
    y = drawRow(page, [
      { x: ML,        w: p1, fill: C.laranja, text: 'Produto',   fn: fontBold, cl: C.branco },
      { x: ML+p1,     w: p2, fill: C.laranja, text: 'Comissão',  fn: fontBold, cl: C.branco, al: 'r' },
      { x: ML+p1+p2,  w: p3, fill: C.laranja, text: '% do Total', fn: fontBold, cl: C.branco, al: 'r' },
    ], y);
    porProduto.forEach((p, i) => {
      if (novaPagina(50)) { /* cabeçalho simples reposto a seguir */ }
      const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
      y = drawRow(page, [
        { x: ML,       w: p1, fill, text: p.name },
        { x: ML+p1,    w: p2, fill, text: brl(p.value), al: 'r' },
        { x: ML+p1+p2, w: p3, fill, text: `${((p.value / totalProd) * 100).toFixed(1)}%`, al: 'r' },
      ], y);
    });
    y -= 14;
  }

  // ── Resumo Geral por Vendedor ─────────────────────────────────────────────
  novaPagina(120);
  secTitulo('RESUMO GERAL POR VENDEDOR');
  const g1 = CW * 0.40; const g2 = CW * 0.20; const g3 = CW * 0.20; const g4 = CW - g1 - g2 - g3;
  const cabGeral = () => {
    y = drawRow(page, [
      { x: ML,           w: g1, fill: C.laranja, text: 'Vendedor',   fn: fontBold, cl: C.branco },
      { x: ML+g1,        w: g2, fill: C.laranja, text: 'Abertura',   fn: fontBold, cl: C.branco, al: 'r' },
      { x: ML+g1+g2,     w: g3, fill: C.laranja, text: 'Fechamento', fn: fontBold, cl: C.branco, al: 'r' },
      { x: ML+g1+g2+g3,  w: g4, fill: C.laranja, text: 'Total',      fn: fontBold, cl: C.branco, al: 'r' },
    ], y);
  };
  cabGeral();
  let tgA = 0; let tgF = 0; let tgT = 0;
  tabelaGeral.forEach((r, i) => {
    if (novaPagina(50)) cabGeral();
    const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
    y = drawRow(page, [
      { x: ML,          w: g1, fill, text: (r.vendedor || '').substring(0, 46) },
      { x: ML+g1,       w: g2, fill, text: brl(r.abertura), al: 'r' },
      { x: ML+g1+g2,    w: g3, fill, text: brl(r.fechamento), al: 'r' },
      { x: ML+g1+g2+g3, w: g4, fill, text: brl(r.total), al: 'r' },
    ], y);
    tgA += r.abertura || 0; tgF += r.fechamento || 0; tgT += r.total || 0;
  });
  if (novaPagina(50)) cabGeral();
  y = drawRow(page, [
    { x: ML,          w: g1, fill: C.laranjaClaro, bc: C.laranja, text: 'Total Geral', fn: fontBold, cl: C.laranjaEsc },
    { x: ML+g1,       w: g2, fill: C.laranjaClaro, bc: C.laranja, text: brl(tgA), fn: fontBold, cl: C.laranjaEsc, al: 'r' },
    { x: ML+g1+g2,    w: g3, fill: C.laranjaClaro, bc: C.laranja, text: brl(tgF), fn: fontBold, cl: C.laranjaEsc, al: 'r' },
    { x: ML+g1+g2+g3, w: g4, fill: C.laranjaClaro, bc: C.laranja, text: brl(tgT), fn: fontBold, cl: C.laranjaEsc, al: 'r' },
  ], y);
  y -= 16;

  // ── Detalhamento por Vendedor ─────────────────────────────────────────────
  const d1 = CW * 0.26; const d2 = CW * 0.16; const d3 = CW * 0.16;
  const d4 = CW * 0.16; const d5 = CW * 0.14; const d6 = CW - d1 - d2 - d3 - d4 - d5;
  const cabDet = () => {
    y = drawRow(page, [
      { x: ML,                w: d1, fill: C.laranja, text: 'Cliente',    fn: fontBold, cl: C.branco },
      { x: ML+d1,             w: d2, fill: C.laranja, text: 'Produto',    fn: fontBold, cl: C.branco },
      { x: ML+d1+d2,          w: d3, fill: C.laranja, text: 'Abertura',   fn: fontBold, cl: C.branco, al: 'r' },
      { x: ML+d1+d2+d3,       w: d4, fill: C.laranja, text: 'Fechamento', fn: fontBold, cl: C.branco, al: 'r' },
      { x: ML+d1+d2+d3+d4,    w: d5, fill: C.laranja, text: 'Total',      fn: fontBold, cl: C.branco, al: 'r' },
      { x: ML+d1+d2+d3+d4+d5, w: d6, fill: C.laranja, text: 'Data Venda', fn: fontBold, cl: C.branco },
    ], y);
  };

  detalhado.forEach((v) => {
    novaPagina(90);
    drawRect(page, ML, y + 2, CW, 16, C.laranjaClaro);
    drawText(page, `${v.vendedor} — ${brl(v.total)}`, ML + 6, y + 7, 8, fontBold, C.laranjaEsc);
    y -= 16;
    cabDet();
    v.linhas.forEach((l, i) => {
      if (novaPagina(50)) cabDet();
      const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
      y = drawRow(page, [
        { x: ML,                w: d1, fill, text: (l.cliente || '').substring(0, 30) },
        { x: ML+d1,             w: d2, fill, text: l.produto },
        { x: ML+d1+d2,          w: d3, fill, text: brl(l.abertura), al: 'r' },
        { x: ML+d1+d2+d3,       w: d4, fill, text: brl(l.fechamento), al: 'r' },
        { x: ML+d1+d2+d3+d4,    w: d5, fill, text: brl(l.total), al: 'r' },
        { x: ML+d1+d2+d3+d4+d5, w: d6, fill, text: l.data },
      ], y);
    });
    y -= 12;
  });

  drawFooter(page);

  const pdfBytes = await pdfDoc.save();
  const blob     = new Blob([pdfBytes], { type: 'application/pdf' });
  const url      = URL.createObjectURL(blob);
  const link     = document.createElement('a');
  link.href      = url;
  link.download  = 'relatorio-comissoes.pdf';
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdfRelatorioComissao;